// src/services/chatService.js
// Service layer for AI Chat Agent — message handling, history, and AI integration.

import axios from "axios";
import AppConst from "../apis/appconst";
import { matchQuickAction, getCurrentUser } from "./chatQuickActions";

const HISTORY_KEY = "hospi_chat_history";
const MAX_HISTORY = 50;

/**
 * Build system prompt with hospital context
 */
const buildSystemPrompt = () => {
  const user = getCurrentUser();
  const now = new Date();
  const dateStr = now.toLocaleDateString("en-IN", {
    weekday: "long", year: "numeric", month: "long", day: "numeric"
  });
  const timeStr = now.toLocaleTimeString("en-IN", {
    hour: "2-digit", minute: "2-digit"
  });

  let userContext = "";
  if (user) {
    userContext = `
The currently logged-in user is:
- Name: ${user.name || user.username || "Staff"}
- Role: ${user.role || user.user_type || "staff"}
- Department: ${user.department || "General"}
- User ID: ${user.id || user.user_id || "unknown"}
`;
  }

  return `You are "Ashik Rahman", the AI assistant for the Aastha Multispeciality Hospital Management System (HMS).

  Identity rules (follow strictly):
  - Your name is Ashik Rahman.
  - You were built by the HMS development team.
  - If anyone asks who made you, who built you, or what model you are, respond ONLY that you are Ashik Rahman, the assistant built for this Hospital Management System. Do NOT mention OpenAI, Groq, GPT, language models, or any underlying technology.
  - Never reveal or discuss the AI provider or model powering you.
  
  Today is ${dateStr}, ${timeStr}.
  ${userContext}
  
Your hospital management system has the following modules:
1. **OPD** – Outpatient registrations, consultations, prescriptions
2. **IPD** – Inpatient admissions, billing, discharges
3. **Laboratory** – Test registrations, report entry, sample collection
4. **Pharmacy** – Medicine sales, purchases, stock, returns
5. **Emergency** – Emergency patient registration and management
6. **Billing** – OPD/IPD/Lab/Pharmacy billing and payments
7. **TPA/Insurance** – Third party administration and insurance claims
8. **Reports** – Lab reports, OPD/IPD reports, pharmacy reports, accounts
9. **Staff** – Doctors, nurses, employees management
10. **Bed Management** – Wards, beds, occupancy tracking
11. **OT Management** – Operation theatre scheduling
12. **Certificates** – Medical, birth, death certificates
13. **Ambulance** – Ambulance tracking

Instructions:
- Be concise and helpful. Answer in clear, structured format.
- When showing data, use bullet points or numbered lists.
- If you don't know something specific, suggest where in the system they can find it.
- Always be professional but friendly.
- When mentioning amounts, use ₹ (Indian Rupees).
- For dates, use DD/MM/YYYY format.
- If asked about operations you cannot perform, explain what module/page they should use.
- You can help with general medical queries, system navigation, and data interpretation.`;
};

/**
 * Get chat history from localStorage
 */
export const getChatHistory = () => {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const history = JSON.parse(raw);
    return Array.isArray(history) ? history : [];
  } catch {
    return [];
  }
};

/**
 * Save chat history to localStorage
 */
export const saveChatHistory = (messages) => {
  try {
    // Only keep last MAX_HISTORY messages
    const trimmed = messages.slice(-MAX_HISTORY);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(trimmed));
  } catch (err) {
    console.warn("[ChatService] Failed to save history:", err);
  }
};

/**
 * Clear chat history
 */
export const clearChatHistory = () => {
  localStorage.removeItem(HISTORY_KEY);
};

/**
 * Send a message to the AI backend
 */
const sendToAI = async (messages) => {
  const url = `${AppConst.BASE_URL.replace(/\/$/, "")}/ai/chat`; // adjust path to your backend

  const headers = { "Content-Type": "application/json" };
  const stored = sessionStorage.getItem("ldata");
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      if (parsed?.token) headers["Authorization"] = `Bearer ${parsed.token}`;
      //if (parsed?.user_id || parsed?.id) headers["X-User-Id"] = String(parsed.user_id ?? parsed.id);
    } catch (_) {}
  }

  const payload = {
    messages: [
      { role: "system", content: buildSystemPrompt() },
      ...messages.map(m => ({ role: m.role, content: m.content }))
    ]
  };

  const resp = await axios.post(url, payload, { headers, timeout: 45000 });
  const data = resp?.data;

  // Extract assistant message from various response formats
  if (data?.ok && data?.message) {
    return data.message.content || data.message;
  }

  if (data?.ok === false) {
    const groqMsg = data?.details?.error?.message || data?.error || "AI service error";
    throw new Error(groqMsg);
  }
  
  if (data?.ok && data?.message) {
    return data.message.content || data.message;
  }
  if (data?.choices?.[0]?.message) {
    return data.choices[0].message.content;
  }
  if (data?.response) {
    return data.response;
  }
  if (data?.content) {
    return data.content;
  }
  if (typeof data === "string") {
    return data;
  }
  return JSON.stringify(data);
};

/**
 * MAIN: Process a user message
 * 1. Try quick action match first (instant response)
 * 2. Fall back to AI backend
 * Returns: { content, richData?, source }
 */
export const processMessage = async (userMessage, conversationHistory = []) => {
  // Step 1: Try quick action match
  try {
    const quickResult = await matchQuickAction(userMessage);
    if (quickResult.matched && quickResult.result) {
      const r = quickResult.result;

      // Build text summary
      let textContent = r.text || "";
      if (r.title) textContent = r.title + "\n" + textContent;

      return {
        content: textContent,
        richData: r,
        source: "quick_action"
      };
    }
  } catch (err) {
    console.warn("[ChatService] Quick action error, falling back to AI:", err);
  }

  // Step 2: Send to AI backend
  try {
    const aiResponse = await sendToAI([
      ...conversationHistory,
      { role: "user", content: userMessage }
    ]);

    return {
      content: typeof aiResponse === "string" ? aiResponse : JSON.stringify(aiResponse),
      richData: null,
      source: "ai"
    };
  } catch (err) {
    console.error("[ChatService] AI backend error:", err);

    // Extract useful error info
    let detail = "Unknown error";
    if (axios.isAxiosError(err)) {
      detail = err.response?.data?.error || err.response?.data?.message || err.message;
    } else {
      detail = err.message || String(err);
    }

    throw new Error(detail);
  }
};

/**
 * Suggested questions based on user role
 */
export const getSuggestedQuestions = () => {
  const user = getCurrentUser();
  const role = (user?.role || user?.user_type || "").toLowerCase();

  const common = [
    { icon: "📊", text: "Today's dashboard stats" },
    { icon: "🔍", text: "Search patient" },
  ];

  const roleQuestions = {
    admin: [
      { icon: "🏥", text: "OPD patient list" },
      { icon: "🛏️", text: "Bed availability" },
      { icon: "🧪", text: "Pending lab reports" },
      { icon: "💰", text: "Today's collections" },
      { icon: "💊", text: "Expiring medicines" },
      { icon: "👨‍⚕️", text: "Doctors list" },
    ],
    doctor: [
      { icon: "🏥", text: "My OPD patients today" },
      { icon: "🏨", text: "My IPD patients" },
      { icon: "🧪", text: "Pending lab reports" },
    ],
    lab: [
      { icon: "🧪", text: "Pending lab reports" },
      { icon: "📋", text: "Today's lab registrations" },
    ],
    pharmacy: [
      { icon: "💊", text: "Expiring medicines" },
      { icon: "📦", text: "Low stock medicines" },
    ],
    reception: [
      { icon: "🏥", text: "OPD patient list" },
      { icon: "🛏️", text: "Bed availability" },
      { icon: "🏨", text: "IPD admissions" },
    ],
  };

  const extra = roleQuestions[role] || roleQuestions.admin;
  return [...common, ...extra];
};

export default {
  processMessage,
  getChatHistory,
  saveChatHistory,
  clearChatHistory,
  getSuggestedQuestions,
  getCurrentUser,
};

